import apiClient from './client';
import { CartItem } from '../store/cart.store';
import { CheckoutFormData } from '../store/checkout.store';

export interface CreateOrderResponse {
  id: number;
  orderNumber: string;
  total: number;
  deliveryCost?: number;
  bonusUsed?: number;
  status: string;
  paymentStatus: string;
  createdAt: string;
}

export interface OrderStatusResponse extends CreateOrderResponse {
  items: Array<{
    product_name: string;
    product_price: number;
    quantity: number;
    total: number;
    product_image?: string;
    composition?: string;
  }>;
  delivery_type?: string;
  delivery_address?: {
    city?: string;
    street?: string;
    house?: string;
    apartment?: string;
    postalCode?: string;
  } | null;
  delivery_date?: string;
  delivery_time?: string;
  payment_type?: string;
  comment?: string | null;
  recipient_name?: string | null;
  recipient_phone?: string | null;
  card_text?: string | null;
  history?: Array<{
    status: string;
    comment?: string | null;
    changed_at: string;
  }>;
}

export interface OrdersListItem {
  id: number;
  order_number: string;
  total: number;
  status: string;
  payment_status: string;
  created_at: string;
}

/** Today/tomorrow from backend delivery context; avoids frontend Date() for business logic. */
export interface CreateOrderDeliveryContext {
  todayDate?: string;
}

export async function createOrder(
  formData: CheckoutFormData,
  items: CartItem[],
  deliveryContext?: CreateOrderDeliveryContext
): Promise<CreateOrderResponse> {
  const todayFromBackend = deliveryContext?.todayDate;
  // Date/time are required for both delivery and pickup (backend validates business rules).
  const date = formData.deliveryDate ?? todayFromBackend ?? '';
  const time = formData.deliveryTime ?? '';
  const payload = {
    customer: {
      name: formData.name,
      phone: formData.phone,
      email: formData.email || null,
    },
    delivery: {
      type: formData.deliveryType,
      address: formData.deliveryType === 'delivery' ? formData.address : undefined,
      pickupPointId: formData.pickupPointId || null,
      date,
      time,
      deliveryTimeType: formData.deliveryTimeType ?? null,
    },
    recipient: {
      name: formData.recipientName,
      phone: formData.recipientPhone,
    },
    cardText: formData.cardText,
    comment: formData.comment || '',
    paymentType: (formData.paymentType === 'card_requisites' || formData.paymentType === 'sbp_qr')
      ? formData.paymentType
      : 'sbp_qr',
    useBonuses: Boolean(formData.useBonuses),
    promocode: formData.promocode?.trim() || null,
    items: items.map((item) => ({
      productId: item.productId,
      productName: item.productName,
      price: item.price,
      quantity: item.quantity,
      image: item.image || null,
    })),
  };

  // Оформление заказа может занимать 30–90 с (Posiflora: создание товаров, заказа, синхронизация клиента)
  const response = await apiClient.post('/api/orders', payload, { timeout: 90000 });
  return response.data.data;
}

export async function getOrderStatus(orderId: number): Promise<OrderStatusResponse> {
  const response = await apiClient.get(`/api/orders/${orderId}`);
  return response.data.data;
}

export async function getOrders(): Promise<OrdersListItem[]> {
  const response = await apiClient.get('/api/orders');
  return response.data.data.orders;
}

export async function uploadReceipt(
  orderId: number,
  imageDataUrl: string,
  fileName?: string | null
): Promise<{ ok: boolean }> {
  const response = await apiClient.post(`/api/orders/${orderId}/receipt`, {
    imageDataUrl,
    fileName: fileName || null,
  });
  return response.data.data;
}

export async function cancelOrder(orderId: number): Promise<{ success: boolean; message?: string }> {
  const response = await apiClient.post(`/api/orders/${orderId}/cancel`);
  return response.data.data;
}
